import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { LogService } from './log.service';
import { LoggingSettingsService } from './logging-settings.service';
import { LogLevel } from 'src/app/enums/log/log-level.enum';
import { ApplicationInsightsService } from './application-insights.service';
import { ToastLoggingService } from './toast-logging.service';

/**
 * Orchestrates a time-bound debug logging session.
 * Responsibilities:
 *  - Elevate log level to DEBUG for a fixed duration (default 1 hour)
 *  - Generate and register a diagnostics session id for telemetry correlation
 *  - Emit lifecycle events (Started / Refreshed / Ended)
 *  - Provide idempotent refresh if already in DEBUG
 *  - Revert to default log level automatically when window ends
 */
@Injectable({ providedIn: 'root' })
export class DebugSessionService {
  private timeoutHandle: ReturnType<typeof setTimeout> | null = null;
  private sessionId: string | null = null;
  private readonly durationMs = 60 * 60 * 1000; // 1 hour
  private readonly storageKey = 'debugSession';
  private expiry: number | null = null;

  private isActiveSubject = new BehaviorSubject<boolean>(false);
  readonly isActive$ = this.isActiveSubject.asObservable();

  constructor(
    private logService: LogService,
    private loggingSettings: LoggingSettingsService,
    private appInsights: ApplicationInsightsService,
    private toastLogging: ToastLoggingService
  ) {
    this.restorePersistedSession();
  }

  /** Start or refresh a debug session, returning the active session id */
  startOrRefresh(): string {
    const alreadyDebug = this.loggingSettings.level === LogLevel.DEBUG;

    // Refresh timer
    if (this.timeoutHandle) {
      clearTimeout(this.timeoutHandle);
    }

    const newExpiry = Date.now() + this.durationMs;
    if (!alreadyDebug || !this.sessionId) {
      this.sessionId = this.generateSessionId();
      this.expiry = newExpiry;
      this.persist();
      this.appInsights.startDiagnosticsSession(this.sessionId);
      this.loggingSettings.setLevel(LogLevel.DEBUG);
      const endIso = new Date(newExpiry).toISOString();
      const endStr = this.formatEndTime(endIso);
      this.toastLogging.success('DebugSession.Started', `Debug logging enabled until ${endStr}.`,  { sessionId: this.sessionId, endsAt: endIso });
    } else {
      this.expiry = newExpiry;
      this.persist();
      const endIso = new Date(newExpiry).toISOString();
      const endStr = this.formatEndTime(endIso);
      this.toastLogging.success('DebugSession.Refreshed', `Debug session refreshed until ${endStr}.`, { sessionId: this.sessionId, endsAt: endIso });
    }

    // Schedule revert
    this.scheduleTimeout();
    this.isActiveSubject.next(true);
    return this.sessionId!;
  }

  /** Explicitly end the active debug session early */
  endEarly(): void { if (this.sessionId) this.endInternal('Manual'); }

  private endIfStillDebug() {
    if (this.loggingSettings.level === LogLevel.DEBUG) {
      this.endInternal('Auto');
    }
  }

  private endInternal(sessionEndReason: 'Auto' | 'Manual') {
    this.sessionId = null;
    this.expiry = null;
    if (this.timeoutHandle) {
      clearTimeout(this.timeoutHandle);
      this.timeoutHandle = null;
    }
    this.loggingSettings.reset();
    this.appInsights.stopDiagnosticsSession();
    this.toastLogging.success('DebugSession.Ended', 'Debug session ended. Log level reverted to default.', { sessionEndReason });
    this.clearPersisted();
    this.isActiveSubject.next(false);
  }

  private generateSessionId(): string {
    try {
      const bytes = new Uint8Array(8);
      crypto.getRandomValues(bytes);
      return 'DBG-' + Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
    } catch {
      return 'DBG-' + Math.random().toString(36).substring(2, 12);
    }
  }

  private formatEndTime(iso: string): string {
    try {
      return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return iso;
    }
  }

  private persist() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify({ id: this.sessionId, expiry: this.expiry }));
    } catch (e) {
      this.logService.warn('DebugSession.PersistenceFailed', e);
    }
  }

  private clearPersisted() {
    try {
      localStorage.removeItem(this.storageKey);
    } catch (e) {
      this.logService.warn('DebugSession.PersistenceClearFailed', e);
    }
  }

  private restorePersistedSession() {
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw) as { id?: string; expiry?: number };
      if (!parsed.id || !parsed.expiry) return;
      if (Date.now() >= parsed.expiry) { this.clearPersisted(); return; }
      // Rehydrate
      this.sessionId = parsed.id;
      this.expiry = parsed.expiry;
      this.loggingSettings.setLevel(LogLevel.DEBUG);
      this.appInsights.startDiagnosticsSession(this.sessionId);
      const remaining = this.expiry - Date.now();
      this.timeoutHandle = setTimeout(() => this.endIfStillDebug(), remaining);
      this.isActiveSubject.next(true);
      this.logService.info('DebugSession.Restored', { sessionId: this.sessionId, endsAt: new Date(this.expiry).toISOString() });
    } catch (e) {
      this.logService.warn('DebugSession.RestoreFailed', e);
    }
  }

  private scheduleTimeout() {
    if (this.timeoutHandle) clearTimeout(this.timeoutHandle);
    if (!this.expiry) return;
    const remaining = this.expiry - Date.now();
    this.timeoutHandle = setTimeout(() => this.endIfStillDebug(), remaining);
  }

  get activeSessionId(): string | null { return this.sessionId; }
  get expiresAt(): number | null { return this.expiry; }
}
